<template>
  <div class="row">
    <div class="col-sm-12">
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
            <th v-for="column in sortable.columns" :key="column.name"
              @click="column.sortable ? $emit('sortBy', column.key) : false"
              class="sortable" :class="sortable.sortColumn === column.key ? 'active-sort' : ''"
              :style="column.sortable ? 'padding-right: 12px;' : ''"
              :width="column.width || 'auto'"
              v-tooltip.top-center="column.tooltip || false"
              v-show="column.name !== 'Lager' || showWarehouses"
            >
              <span :style="column.sortable ? 'padding-right: 2px;' : ''">{{ column.name }}</span>
              <i class="fas" style="position: absolute; padding-left: 2px;"
                v-if="column.sortable"
                :class="sortable.sortOrders[column.key] > 0 ? 'fa-sort-up' : 'fa-sort-down'"
              ></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in products" :key="product.id"
            :only-show-action="onlyShowAction"
            is="ProductRow"
            :product="product"
            @openSuppliers="openSuppliers"
            @deleteProduct="$emit('deleteProduct', $event)"
            @openWarehouses="openWarehouses"
            @filterProductCategory="$emit('filterProductCategory', $event)"
            :show-warehouses="showWarehouses"
            @enterEditMode="enterEditMode"
            @exitEditMode="exitEditMode"
            @saveEdit="saveEdit"
            :edit-mode="product.id === editMode"
            :product-categories="productCategories"
            @openAssignToSuppliersDialog="openAssignToSuppliersDialog"
            @openStockRotationModal="openStockRotationModal"
            @openAddInstanceDialog="openAddInstanceDialog"
          />
        </tbody>
      </table>
    </div>
    <product-suppliers-dialog
      :show="showSuppliersDialog"
      :product="selectedProduct"
      @deleteProductSupplier="deleteProductSupplier"
      @close="showSuppliersDialog = false"
    />
    <product-warehouses-dialog
      :show="showWarehousesDialog"
      :product-warehouses="productWarehouses"
      @close="showWarehousesDialog = false"
    />
    <product-variations-warehouses-dialog
      :show="showVariationsWarehousesDialog"
      :product-variations-warehouses="productVariationsWarehouses"
      @close="showVariationsWarehousesDialog = false"
    />
    <assign-to-supplier-dialog v-if="productToAssign"
      :show="showAssignToSuppliersDialog"
      :suppliers="suppliers"
      @assignToSupplier="assignToSupplier"
      @close="closeAssignToSuppliersDialog"
      :val-errors="assignErrors"
      :init-price="productToAssign.standard_price || ''"
    />
    <create-stock-rotations-dialog
      :show="showCreateStockRotationsDialog"
      :product="productToCreateStockRotatios"
      :causes="causesForStockRotation"
      @close="closeCreateStockRotationsDialog"
    />
    <add-product-variation-instance-dialog v-if="productToAddInstance"
      :show="showAddInstanceDialog"
      :product-id="productToAddInstance.id"
      :variations="productToAddInstance.variations"
      @close="closeAddInstanceDialog"
      @created="openLabelsDialog"
    />
  </div>
</template>

<script>
import showLabelsAlert from 'src/productInstancesLabelsAlert'
import ProductSuppliersDialog from './product-suppliers-dialog'
import ProductWarehousesDialog from './product-warehouses-dialog'
import ProductVariationsWarehousesDialog from './product-variations-warehouses-dialog'
import ProductRow from './product-row'
import AssignToSupplierDialog from './assign-to-supplier-dialog'
import CreateStockRotationsDialog from './create-stock-rotations-dialog'
import AddProductVariationInstanceDialog from './add-product-variation-instance-dialog'

export default {
  props: {
    products: Array,
    loading: {
      default: false
    },
    onlyShowAction: {
      default: false
    },
    showWarehouses: {
      default: true
    },
    productCategories: Array,
    sortable: Object
  },
  components: {
    ProductSuppliersDialog,
    ProductWarehousesDialog,
    ProductRow,
    AssignToSupplierDialog,
    CreateStockRotationsDialog,
    AddProductVariationInstanceDialog,
    ProductVariationsWarehousesDialog
  },
  data () {
    return {
      productSuppliers: [],
      productWarehouses: [],
      productVariationsWarehouses: [],
      showSuppliersDialog: false,
      showWarehousesDialog: false,
      showVariationsWarehousesDialog: false,
      selectedProduct: null,
      editMode: null,
      suppliers: [],
      showAssignToSuppliersDialog: false,
      productToAssign: null,
      assignErrors: [],
      productToCreateStockRotatios: null,
      productToAddInstance: null,
      showCreateStockRotationsDialog: false,
      showAddInstanceDialog: false,
      causesForStockRotation: []
    }
  },
  methods: {
    openSuppliers ({ productSuppliers, product }) {
      this.selectedProduct = product
      this.showSuppliersDialog = true
    },
    openWarehouses (product) {
      if (product.has_variations) {
        this.$http.get(`/products/${product.id}/variations/warehouses`).then(({ data }) => {
          this.productVariationsWarehouses = data
          this.showVariationsWarehousesDialog = true
        })
      } else {
        this.$http.get(`/products/${product.id}/warehouses`).then(({ data }) => {
          this.productWarehouses = data
          this.showWarehousesDialog = true
        })
      }
    },
    async deleteProductSupplier (supplier) {
      if (confirm('Zuweisung zum Lieferanten wirklich löschen?')) {
        await this.initCsrf()

        this.$http.delete(`/product_supplier/${supplier.id}`).then(({ data }) => {
          const index = this.selectedProduct.suppliers.findIndex(({ id }) => id === data.id)
          this.selectedProduct.suppliers.splice(index, 1)
        })
      }
    },
    enterEditMode (product) {
      this.editMode = product.id
    },
    exitEditMode (product) {
      this.editMode = null
    },
    saveEdit (product) {
      this.$emit('updateProduct', product)
      this.editMode = null
    },
    openAssignToSuppliersDialog (product) {
      this.productToAssign = product
      let loadSuppliers = Promise.resolve()
      if (!this.suppliers.length) {
        loadSuppliers = this.$http.get(`/suppliers`).then(({ data }) => {
          this.suppliers = data
        })
      }
      loadSuppliers.then(() => { this.showAssignToSuppliersDialog = true })
    },
    async assignToSupplier (payload) {
      await this.initCsrf()

      const url = `/products/${this.productToAssign.id}/suppliers/${payload.supplier_id}`
      this.$http.post(url, { price: payload.price }).then(({ data }) => {
        this.$notify({
          message: 'Produkt erfolgreich dem Lieferanten zugewiesen.',
          type: 'success'
        })
        this.productToAssign.suppliers.unshift(data)
        this.closeAssignToSuppliersDialog()
        // this.productSuppliersFetched.unshift(data)
      }).catch(({ response }) => { this.assignErrors = response.data.errors })
    },
    closeAssignToSuppliersDialog () {
      this.showAssignToSuppliersDialog = false
      this.productToAssign = null
    },
    openStockRotationModal ({ direction, product }) {
      this.showCreateStockRotationsDialog = true
      this.$http.get(`/causes?only_selectable=1&direction=${direction}`).then(({ data }) => {
        this.causesForStockRotation = data
        this.productToCreateStockRotatios = product
      })
    },
    closeCreateStockRotationsDialog () {
      this.showCreateStockRotationsDialog = false
      this.productToCreateStockRotatios = null
      this.causesForStockRotation = []
    },
    openAddInstanceDialog (product) {
      this.productToAddInstance = null
      this.showAddInstanceDialog = true
      this.$http.get(`/products/${product.id}`).then(({ data }) => {
        data.variations = data.variations.map(variation => {
          variation.instancesToCreate = null
          return variation
        })
        this.productToAddInstance = data
      })
    },
    closeAddInstanceDialog () {
      this.showAddInstanceDialog = false
    },
    openLabelsDialog (data) {
      this.closeAddInstanceDialog()
      this.$notify({
        message: 'Die Instanzierung wurden erfolgreich erfasst.',
        type: 'success'
      })
      showLabelsAlert(data)
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
}

.active-sort {
  font-weight: bold;
}
</style>
